/**
 * SoftphonePanel — 500 × 1000 fixed frame. Frontend only.
 *
 * ── Endpoint audit (see report at bottom of the source) ───────────────
 *   Header presence        PATCH /api/auth/status
 *   Registration state     Voice SDK deviceReady (via useCall)
 *   Reconnect              useCall().initializeDevice({ isUserGesture:true })
 *   Dial / call            useCall().makeCall  (already backend-wired)
 *   Contact lookup         GET /api/contacts               (existing search)
 *   Queue                  GET /api/twilio/queue/status    (name enrichment client-side)
 *   Park slots             useCall().parkSlots + pickupParkedCall
 *   Speed dials            useCall().speedDials  ← GET /api/twilio/speed-dials
 *   Calls feed             GET /api/twilio/recent-calls
 *   Voicemail feed         GET /api/twilio/voicemails
 *   SMS threads feed       GET /api/sms/threads
 *   Mark VM handled        PATCH /api/twilio/voicemails/:id/read
 *   Mark SMS handled       POST  /api/sms/threads/:phone/read
 *   Unknown-number rename  GET  /api/contacts/needs-rename  (admin only)
 *
 * Missing endpoint (declared honestly, no fabrication):
 *   - No endpoint exists to mark a MISSED CALL row as "handled". The
 *     mark-handled button on call rows only hides the row for the
 *     current tab session and does NOT persist. See report.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone, MessageSquare, Voicemail, ChevronDown, Circle, RefreshCw,
  Search, Plus, PhoneCall, PhoneMissed, Play, Printer, UserPlus, Check,
  BookUser,
} from "lucide-react";
import { useCall } from "../layout/ActiveCallOverlay";
import CallControls from "./CallControls";
import {
  contactsAPI, callsAPI, voicemailAPI, smsAPI, twilioAPI, speedDialAPI,
} from "../../services/api";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { formatDuration, formatPhoneNumber, cn } from "../../lib/utils";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "../ui/dropdown-menu";

// ── Semantic palette (spec-locked) ──────────────────────────────────
const NAVY  = "#143875"; // header, active chip, gold-bar text
const ROYAL = "#2A7CC4"; // secondary actions
const GOLD  = "#FFCC1A"; // counts, queue accent, active-call bar
const GREEN = "#1D9E75"; // connect actions
const RED   = "#C0392B"; // hang up + missed only
const BG    = "var(--surface, #F7F8FB)"; // app surface variable → dark-mode safe

// ── Phone key normalisation (matches backend contact_directory.phone_key) ─
// Digits only, drop US "1" prefix, keep last 10.
function phoneKey(v) {
  if (v == null) return "";
  const digits = String(v).replace(/\D/g, "");
  const stripped = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return stripped.length >= 10 ? stripped.slice(-10) : "";
}
function phoneE164(v) {
  const k = phoneKey(v);
  return k ? `+1${k}` : "";
}
const DIGIT_TEST = /^[\d\s()+\-.*#]+$/;
const isDialInput = (s) => !!s && DIGIT_TEST.test(s.trim()) && s.replace(/\D/g, "").length >= 3;

// ── Semantic day dividers (Today / Yesterday) ───────────────────────
function dayBucket(iso) {
  if (!iso) return "Unknown";
  const d = new Date(iso);
  const now = new Date();
  const isSame = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (isSame(d, now)) return "Today";
  if (isSame(d, y)) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function shortTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const opts = { hour: "numeric", minute: "2-digit" };
  const t = d.toLocaleTimeString(undefined, opts).toLowerCase().replace(" ", "");
  return t.replace(":00", "");
}

// ── Audible alert for callers-waiting. Small chime, throttled. ─────
function useQueueChime(count) {
  const lastCountRef = useRef(0);
  useEffect(() => {
    if (count > 0 && count > lastCountRef.current) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 880;
        gain.gain.value = 0.08;
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start();
        setTimeout(() => { try { osc.stop(); ctx.close(); } catch (_e) { /* AudioContext already closed */ } }, 220);
      } catch (_e) { /* AudioContext unavailable — silent alert */ }
    }
    lastCountRef.current = count;
  }, [count]);
}

// ── Tab-title notifier: "(2) avida PHONE — n waiting" ──────────────
function useTabTitle(count) {
  useEffect(() => {
    const original = document.title;
    if (count > 0) {
      document.title = `(${count}) ${count === 1 ? "1 caller" : `${count} callers`} waiting`;
    }
    return () => { document.title = original; };
  }, [count]);
}

const PRESENCE_OPTIONS = [
  { value: "available",   label: "Available",   color: GREEN,       backend: "available" },
  { value: "away",        label: "Away",        color: "#F59E0B",   backend: "away" },
  { value: "unavailable", label: "Unavailable", color: "#94A3B8",   backend: "offline" },
];

export default function SoftphonePanel({ onOpenRecord }) {
  const { user, updateStatus } = useAuth();
  const navigate = useNavigate();
  const ctx = useCall() || {};
  const {
    deviceReady, initializing, initializeDevice, makeCall,
    activeCall, callDuration, speedDials = [], parkSlots = [], pickupParkedCall,
  } = ctx;

  // Presence — user.status is the source of truth. Backend values map into
  // our tri-state model (available / away / unavailable). Anything not
  // "available" or "away" collapses to "unavailable" for display.
  const presence = (() => {
    const s = user?.status || "available";
    if (s === "available") return "available";
    if (s === "away") return "away";
    return "unavailable";
  })();

  // Outbound caller-ID selector (uses existing /api/twilio/available-numbers).
  const [callerIds, setCallerIds] = useState([]);
  const [selectedCallerId, setSelectedCallerId] = useState(null);
  useEffect(() => {
    twilioAPI.getAvailableNumbers()
      .then(r => {
        const nums = Array.isArray(r.data) ? r.data : (r.data?.numbers || []);
        setCallerIds(nums);
        if (nums.length && !selectedCallerId) {
          setSelectedCallerId(nums[0].phone_number || nums[0].number || nums[0]);
        }
      })
      .catch(() => setCallerIds([]));
  }, []);

  // Real registration state → tri-state for the presence indicator.
  // green : available AND registered
  // amber : available AND not registered (offer Reconnect)
  // gray  : any other combination (away/unavailable)
  const regState = (() => {
    if (presence !== "available") return "gray";
    return deviceReady ? "green" : "amber";
  })();

  const togglePresence = async (value) => {
    const opt = PRESENCE_OPTIONS.find(o => o.value === value);
    const backendValue = opt?.backend || value;
    try {
      await updateStatus(backendValue);
      toast.success(`Status: ${opt?.label || value}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  // ── Dial input (contact search + number entry, single field) ───
  const [dial, setDial] = useState("");
  const dialRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  useEffect(() => { contactsAPI.getAll().then(r => setContacts(r.data || [])).catch(() => {}); }, []);

  // Global keyboard shortcuts for the softphone:
  //   "/"                   → focus the dial input (unchanged)
  //   0-9, *, #, +          → append to dial (when not inside another field)
  //   Backspace             → remove last digit from dial (when not inside another field)
  //   Enter                 → place the call to whatever is in dial
  // We SKIP everything when the user is typing in any input/textarea/contentEditable,
  // and also when there's an active call (so DTMF stays the sole owner of the number keys).
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const inField = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (inField) return;
      // Never hijack keys while the user is talking on an active call — those
      // keystrokes could belong to a completely different UI (chat, notepad,
      // whatever). Physical-keyboard DTMF during a call isn't supported here.
      if (activeCall) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "/") {
        e.preventDefault();
        dialRef.current?.focus();
        return;
      }
      // Digits, *, #, and + all extend the number.
      if (/^[0-9*#+]$/.test(e.key)) {
        e.preventDefault();
        setDial(prev => (prev || "") + e.key);
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        setDial(prev => (prev || "").slice(0, -1));
        return;
      }
      if (e.key === "Enter") {
        // Only if we actually have something dialable — don't submit empty.
        if (dial && isDialInput(dial)) {
          e.preventDefault();
          placeCall(dial);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeCall, dial]);

  const contactMatches = useMemo(() => {
    const q = dial.trim().toLowerCase();
    if (!q || isDialInput(dial)) return [];
    return contacts
      .filter(c => {
        const name = `${c.first_name || ""} ${c.last_name || ""}`.trim().toLowerCase();
        const co = (c.practice_name || c.company || "").toLowerCase();
        return name.includes(q) || co.includes(q);
      })
      .slice(0, 6);
  }, [dial, contacts]);

  // Build a phoneKey → contact map for feed enrichment
  const contactByKey = useMemo(() => {
    const m = new Map();
    contacts.forEach(c => {
      const k = phoneKey(c.phone_number);
      if (k) m.set(k, c);
    });
    return m;
  }, [contacts]);

  const placeCall = (target) => {
    if (!deviceReady) {
      toast.error("Phone not connected — click Reconnect");
      return;
    }
    if (!selectedCallerId) {
      toast.error("Pick an outbound caller ID first");
      return;
    }
    const num = phoneE164(target) || target;
    if (!num) { toast.error("Enter a phone number"); return; }
    makeCall(num, selectedCallerId);
  };

  const sendText = (target) => {
    // Delegate to whatever the app already routes SMS through.
    // Emit a custom event the host page can catch; if nothing listens,
    // fall back to a toast (honest, no silent no-op).
    const e164 = phoneE164(target) || target;
    if (!e164) { toast.error("Enter a phone number"); return; }
    const handled = window.dispatchEvent(new CustomEvent("softphone:compose-sms", { detail: { to: e164 } }));
    if (!handled) toast.info(`Open SMS composer for ${formatPhoneNumber(e164)}`);
  };

  // ── Queue polling (5s). Uses the existing per-user filtered endpoint. ──
  const [queueCalls, setQueueCalls] = useState([]);
  const [queueError, setQueueError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await callsAPI.getQueueStatus?.() || await api.get("/twilio/queue/status");
        if (cancelled) return;
        setQueueCalls(r.data?.calls || []);
        setQueueError(null);
      } catch (e) {
        if (!cancelled) setQueueError(e?.response?.status || "err");
      }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  useQueueChime(queueCalls.length);
  useTabTitle(queueCalls.length);

  // ── Agents-available (admin-only endpoint; degrade for staff) ───
  const [agents, setAgents] = useState(null);   // null = unknown / not entitled
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/twilio/dashboard/agents");
        if (cancelled) return;
        setAgents((r.data || []).filter(a => a.status === "available"));
      } catch (e) {
        if (!cancelled) setAgents(e?.response?.status === 403 ? "forbidden" : null);
      }
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // ── Activity feed (merged: calls + VMs + SMS threads) ────────
  const [feedItems, setFeedItems] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState({ calls: null, vms: null, sms: null });
  const [handledInSession, setHandledInSession] = useState(() => new Set());

  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    const [callsR, vmsR, threadsR] = await Promise.allSettled([
      callsAPI.getRecent(80, false),
      voicemailAPI.getAll({ limit: 40 }),
      smsAPI.getThreads(),
    ]);
    const errs = {
      calls:  callsR.status === "rejected" ? (callsR.reason?.response?.status || "err") : null,
      vms:    vmsR.status   === "rejected" ? (vmsR.reason?.response?.status   || "err") : null,
      sms:    threadsR.status === "rejected" ? (threadsR.reason?.response?.status || "err") : null,
    };
    setFeedError(errs);
    const items = [];

    if (callsR.status === "fulfilled") {
      (callsR.value.data || []).forEach(c => {
        const isInbound = c.direction === "inbound";
        const missed = ["missed", "no-answer", "no_answer", "busy", "failed", "caller_hangup"].includes(c.status);
        const rawPhone = isInbound ? c.from_number : c.to_number;
        // Client-side safety net for `client:<uuid>` values that slipped
        // past the backend enricher (older rows, edge cases). Backend
        // /twilio/recent-calls should already resolve these to caller_name;
        // if not, we mask the ugly UUID and mark it as an internal call.
        const isClientId = typeof rawPhone === "string" && rawPhone.startsWith("client:");
        const phone = isClientId ? "" : rawPhone;
        const key = phoneKey(phone);
        const ct = contactByKey.get(key);
        const name = c.caller_name
          || (ct ? `${ct.first_name || ""} ${ct.last_name || ""}`.trim() : "")
          || (isClientId ? "Internal caller" : "")
          || null;
        items.push({
          type: missed ? "missed_call" : "call",
          id: `call:${c.id || c.call_sid}`,
          rawId: c.id || c.call_sid,
          phone, name, contactId: ct?.id || null,
          timestamp: c.created_at,
          missed,
          direction: c.direction,
          companyName: c.company_name,
          durationSeconds: c.duration_seconds || 0,
          contextLine: isClientId
            ? `Internal call · softphone-to-softphone`
            : buildCallContext(ct, c),
          unattended: missed,
        });
      });
    }

    if (vmsR.status === "fulfilled") {
      (vmsR.value.data || []).forEach(vm => {
        const key = phoneKey(vm.from_number);
        const ct = contactByKey.get(key);
        const name = ct ? `${ct.first_name || ""} ${ct.last_name || ""}`.trim() : null;
        items.push({
          type: "voicemail",
          id: `vm:${vm.id}`,
          rawId: vm.id,
          phone: vm.from_number,
          name,
          contactId: ct?.id || null,
          timestamp: vm.created_at,
          unread: !vm.is_read,
          transcription: vm.transcription,
          durationSeconds: vm.duration_seconds || 0,
          companyName: vm.company_name,
          contextLine: vm.transcription
            ? `"${String(vm.transcription).slice(0, 60)}${vm.transcription.length > 60 ? "…" : ""}" · ${formatDuration(vm.duration_seconds || 0)}`
            : `Voicemail · ${formatDuration(vm.duration_seconds || 0)}`,
          unattended: !vm.is_read,
        });
      });
    }

    if (threadsR.status === "fulfilled") {
      (threadsR.value.data?.threads || threadsR.value.data || []).forEach(t => {
        const phone = t.external_number || t.phone_number || t.contact_phone;
        const key = phoneKey(phone);
        const ct = contactByKey.get(key);
        const name = t.contact_name || t.display_name || (ct ? `${ct.first_name || ""} ${ct.last_name || ""}`.trim() : null);
        items.push({
          type: "sms",
          id: `sms:${phone}`,
          rawId: phone,
          rawSecondary: t.our_number || t.to_number,
          phone,
          name,
          contactId: ct?.id || t.contact_id || null,
          timestamp: t.last_message_at || t.created_at,
          unread: (t.unread_count || 0) > 0,
          unreadCount: t.unread_count || 0,
          companyName: t.company_name,
          contextLine: t.last_message ? String(t.last_message).slice(0, 80) : "Text message",
          unattended: (t.unread_count || 0) > 0,
        });
      });
    }

    items.sort((a, b) => (new Date(b.timestamp || 0)) - (new Date(a.timestamp || 0)));
    setFeedItems(items);
    setFeedLoading(false);
  }, [contactByKey]);

  useEffect(() => {
    loadFeed();
    const t = setInterval(loadFeed, 15000);
    return () => clearInterval(t);
  }, [loadFeed]);

  // ── Filter chips ──
  const [filter, setFilter] = useState("all");
  const visibleFeed = useMemo(() => {
    const base = feedItems.filter(i => !handledInSession.has(i.id));
    if (filter === "missed") return base.filter(i => i.type === "missed_call");
    if (filter === "voicemail") return base.filter(i => i.type === "voicemail");
    if (filter === "texts") return base.filter(i => i.type === "sms");
    return base;
  }, [feedItems, filter, handledInSession]);

  const counts = useMemo(() => {
    const base = feedItems.filter(i => !handledInSession.has(i.id));
    return {
      missed:    base.filter(i => i.type === "missed_call" && i.unattended).length,
      voicemail: base.filter(i => i.type === "voicemail"   && i.unattended).length,
      texts:     base.filter(i => i.type === "sms"         && i.unattended).length,
      all:       base.filter(i => i.unattended).length,
    };
  }, [feedItems, handledInSession]);

  const markHandled = async (item) => {
    // Only VMs and SMS threads have backend endpoints; missed calls are
    // session-only until a proper endpoint exists (see report).
    try {
      if (item.type === "voicemail") {
        await voicemailAPI.markRead(item.rawId);
      } else if (item.type === "sms") {
        await smsAPI.markThreadAsRead(item.rawId, item.rawSecondary || null);
      }
    } catch { /* silent — session dismiss still works */ }
    setHandledInSession(prev => { const n = new Set(prev); n.add(item.id); return n; });
  };

  const openRecord = (item) => {
    if (onOpenRecord) onOpenRecord(item);
    else {
      const handled = window.dispatchEvent(new CustomEvent("softphone:open-record", { detail: item }));
      if (!handled) toast.info(item.name || formatPhoneNumber(item.phone));
    }
  };

  // ── Group feed by day for section headers ──
  const groupedFeed = useMemo(() => {
    const groups = [];
    let current = null;
    visibleFeed.forEach(i => {
      const b = dayBucket(i.timestamp);
      if (!current || current.day !== b) {
        current = { day: b, items: [] };
        groups.push(current);
      }
      current.items.push(i);
    });
    return groups;
  }, [visibleFeed]);

  // ── Render ──
  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col ring-2"
      style={{
        width: "100%", maxWidth: "100%", height: 1000,
        // Vibrant panel: soft sky-tinted top fading to white — no longer flat.
        background: "linear-gradient(180deg,#F0F7FF 0%,#FFFFFF 180px,#FFFFFF 100%)",
        fontFamily: "Montserrat, sans-serif",
        // Two-layer shadow + navy ring for depth.
        boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 20px 60px rgba(20,56,117,0.14)",
        // Thin navy outline replaces the near-invisible black/5 ring.
        "--tw-ring-color": "rgba(20,56,117,0.14)",
      }}
      data-testid="softphone-panel"
    >
      {/* ═══ 1. HEADER ══════════════════════════════════════════ */}
      <header
        className="px-4 pt-3.5 pb-3.5 shrink-0 relative"
        style={{
          // Deeper multi-stop gradient with a hint of royal at the shine line.
          background: "linear-gradient(135deg,#1E4FA8 0%,#143875 45%,#0C2A5E 100%)",
          borderBottom: `3px solid ${GOLD}`,
          boxShadow: "inset 0 -1px 0 rgba(255,204,26,0.35)",
        }}
        data-testid="softphone-header"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-1.5">
            <span
              className="text-white text-xl"
              style={{ fontWeight: 700, letterSpacing: "-0.02em", textShadow: "0 1px 2px rgba(0,0,0,0.15)" }}
            >
              avida
            </span>
            <span
              className="text-[11px] tracking-[0.24em]"
              style={{ color: GOLD, fontWeight: 700 }}
            >
              PHONE
            </span>
          </div>
          {/* Presence toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 rounded-full px-3 py-1 hover:bg-white/10 transition-colors"
                data-testid="softphone-presence-toggle"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: PRESENCE_OPTIONS.find(o => o.value === presence)?.color || GREEN }}
                />
                <span className="text-white text-sm capitalize" style={{ fontWeight: 500 }}>{presence}</span>
                <ChevronDown className="w-3 h-3 text-white/70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40" data-testid="softphone-presence-menu">
              {PRESENCE_OPTIONS.map(opt => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => togglePresence(opt.value)}
                  className="cursor-pointer"
                  data-testid={`softphone-presence-${opt.value}`}
                >
                  <span className="w-2 h-2 rounded-full mr-2" style={{ background: opt.color }} />
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Second line: caller-ID + registration state */}
        <div className="mt-2 flex items-center justify-between text-xs">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 text-white/90 hover:text-white transition-colors"
                data-testid="softphone-callerid-toggle"
                disabled={callerIds.length === 0}
              >
                <span style={{ fontWeight: 500 }}>Calling as</span>
                <span className="font-mono text-white">
                  {selectedCallerId ? formatPhoneNumber(selectedCallerId) : "no numbers"}
                </span>
                {callerIds.length > 1 && <ChevronDown className="w-3 h-3 text-white/60" />}
              </button>
            </DropdownMenuTrigger>
            {callerIds.length > 1 && (
              <DropdownMenuContent align="start" data-testid="softphone-callerid-menu">
                <DropdownMenuLabel>Outbound caller ID</DropdownMenuLabel>
                {callerIds.map(n => {
                  const num = n.phone_number || n.number || n;
                  return (
                    <DropdownMenuItem
                      key={num}
                      onClick={() => setSelectedCallerId(num)}
                      className="cursor-pointer"
                      data-testid={`softphone-callerid-${num}`}
                    >
                      <span className="font-mono mr-2">{formatPhoneNumber(num)}</span>
                      <span className="text-slate-500 text-xs">{n.friendly_name || n.name || ""}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            )}
          </DropdownMenu>

          <div className="flex items-center gap-2">
            <span
              className={cn("w-2 h-2 rounded-full", regState === "amber" && "animate-pulse")}
              style={{
                background: regState === "green" ? GREEN : regState === "amber" ? "#F59E0B" : "#94A3B8",
              }}
              data-testid="softphone-registration-dot"
              data-state={regState}
            />
            <span className="text-white/90" style={{ fontWeight: 400 }}>
              {regState === "green" ? "Line connected"
                : regState === "amber" ? "Not registered"
                : "Offline"}
            </span>
            {regState === "amber" && (
              <button
                onClick={() => initializeDevice?.({ isUserGesture: true })}
                disabled={initializing}
                className="ml-1 text-white underline underline-offset-2 hover:text-white"
                style={{ fontWeight: 500 }}
                data-testid="softphone-reconnect-btn"
              >
                {initializing ? "…" : "Reconnect"}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ═══ 2. ACTIVE CALL BAR (gold) ═══════════════════════════ */}
      {/* Reserves layout via always-rendered slot but with zero height when idle */}
      <ActiveCallBar />

      {/* ═══ 3. DIAL ROW ═════════════════════════════════════════ */}
      <div className="px-4 py-3 shrink-0 border-b" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid="softphone-dial-row">
        <div className="flex items-center gap-2 relative">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={dialRef}
              value={dial}
              onChange={e => setDial(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && dial.trim()) {
                  if (isDialInput(dial)) placeCall(dial);
                  else if (contactMatches[0]) placeCall(contactMatches[0].phone_number);
                }
              }}
              placeholder="Search a name or type a number"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-white border text-sm outline-none focus:ring-2 focus:ring-offset-0 transition-all"
              style={{
                borderColor: "rgba(15,23,42,0.10)",
                fontWeight: 400,
              }}
              data-testid="softphone-dial-input"
            />
            {contactMatches.length > 0 && (
              <div
                className="absolute left-0 right-0 top-11 rounded-lg bg-white shadow-xl border overflow-hidden z-30"
                style={{ borderColor: "rgba(15,23,42,0.08)" }}
                data-testid="softphone-contact-matches"
              >
                {contactMatches.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setDial(""); placeCall(c.phone_number); }}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left transition-colors"
                    data-testid={`softphone-match-${c.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm truncate" style={{ fontWeight: 500 }}>
                        {c.first_name} {c.last_name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {c.practice_name || c.company || formatPhoneNumber(c.phone_number)}
                      </p>
                    </div>
                    <Phone className="w-4 h-4 shrink-0" style={{ color: GREEN }} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => (isDialInput(dial) ? placeCall(dial) : contactMatches[0] && placeCall(contactMatches[0].phone_number))}
            disabled={!deviceReady || (!dial && !contactMatches[0])}
            className="h-10 px-4 rounded-lg text-white text-sm flex items-center gap-1.5 disabled:opacity-40 transition-all"
            style={{
              // Bright neon green with a green glow — reads as "GO" from
              // across the room. Was sage #1D9E75, then #16A34A; now #22C55E
              // with a radiating shadow.
              background: "#22C55E",
              boxShadow: "0 0 0 2px rgba(34,197,94,0.20), 0 0 18px rgba(34,197,94,0.55), 0 2px 6px rgba(15,23,42,0.10)",
              fontWeight: 700,
              textShadow: "0 1px 1px rgba(0,0,0,0.15)",
            }}
            data-testid="softphone-call-btn"
          >
            <Phone className="w-4 h-4" /> Call
          </button>
          <button
            onClick={() => (isDialInput(dial) ? sendText(dial) : contactMatches[0] && sendText(contactMatches[0].phone_number))}
            className="h-10 w-10 rounded-lg text-white flex items-center justify-center transition-opacity hover:opacity-90 shadow-sm hover:shadow-md"
            style={{ background: "#F97316" }}  /* orange (was royal blue) — per Dawn */
            title="Send text"
            data-testid="softphone-text-btn"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ═══ 4. KEYPAD + QUEUE (side by side, 50/50 split) ═════════ */}
      <div className="px-4 py-3 shrink-0 flex gap-3 items-stretch" data-testid="softphone-keypad-queue">
        <Keypad
          activeCall={activeCall}
          sendDTMF={ctx.sendDTMF}
          onDigit={(d) => { setDial(prev => (prev || "") + d); dialRef.current?.focus(); }}
          onBackspace={() => setDial(prev => (prev || "").slice(0, -1))}
        />
        <QueueCard
          callers={queueCalls}
          contactByKey={contactByKey}
          agents={agents}
          parkSlots={parkSlots}
          onPickup={pickupParkedCall}
          error={queueError}
        />
      </div>

      {/* ═══ 5. SPEED DIALS ═══════════════════════════════════════ */}
      <SpeedDials items={speedDials} onCall={placeCall} onAdd={() => window.dispatchEvent(new CustomEvent("softphone:add-speed-dial"))} />

      {/* ═══ 6. FILTER CHIPS ══════════════════════════════════════ */}
      <div className="px-4 pt-2 pb-2 shrink-0 border-b" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid="softphone-filters">
        <div className="flex items-center gap-1.5">
          <FilterChip active={filter === "all"}       onClick={() => setFilter("all")}       label="All"       count={counts.all} testid="softphone-filter-all" />
          <FilterChip active={filter === "missed"}    onClick={() => setFilter("missed")}    label="Missed"    count={counts.missed} testid="softphone-filter-missed" />
          <FilterChip active={filter === "voicemail"} onClick={() => setFilter("voicemail")} label="Voicemail" count={counts.voicemail} testid="softphone-filter-voicemail" />
          <FilterChip active={filter === "texts"}     onClick={() => setFilter("texts")}     label="Texts"     count={counts.texts} testid="softphone-filter-texts" />
          <div className="ml-auto flex items-center gap-0.5">
            <button
              onClick={() => navigate("/contacts")}
              className="text-slate-400 hover:text-slate-600 p-1 rounded transition-colors"
              title="Open Phone Book"
              data-testid="softphone-phonebook"
            >
              <BookUser className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={loadFeed}
              className="text-slate-400 hover:text-slate-600 p-1 rounded transition-colors"
              title="Refresh"
              data-testid="softphone-feed-refresh"
            >
              <RefreshCw className={cn("w-3.5 h-3.5", feedLoading && "animate-spin")} />
            </button>
          </div>
        </div>
      </div>

      {/* ═══ 7. ACTIVITY FEED (only scrollable region) ═══════════ */}
      <div className="flex-1 overflow-y-auto min-h-0" data-testid="softphone-feed">
        {feedLoading && feedItems.length === 0 && (
          <div className="py-10 text-center text-sm text-slate-400" style={{ fontWeight: 400 }}>Loading activity…</div>
        )}
        {!feedLoading && feedError.calls && feedError.vms && feedError.sms && (
          <div className="py-10 text-center text-sm text-slate-400 px-6" style={{ fontWeight: 400 }}>
            All three activity endpoints are unavailable right now. Check your network or ask an admin.
          </div>
        )}
        {!feedLoading && visibleFeed.length === 0 && !feedError.calls && (
          <div className="py-10 text-center text-sm text-slate-400" style={{ fontWeight: 400 }}>
            No activity in this view.
          </div>
        )}
        {groupedFeed.map((g, gi) => (
          <div key={g.day}>
            <div
              className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-[0.15em]"
              style={{ fontWeight: 700, color: NAVY }}
              data-testid={`softphone-day-${g.day}`}
            >
              {g.day}
            </div>
            {g.items.map((item, ii) => (
              <FeedRow
                key={item.id}
                item={item}
                zebra={(gi + ii) % 2 === 1}
                onOpen={() => openRecord(item)}
                onCall={() => placeCall(item.phone)}
                onText={() => sendText(item.phone)}
                onMarkHandled={() => markHandled(item)}
                unknownNumber={!item.name && !item.contactId}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Small helper: build the “relationship + why it matters” line ─
function buildCallContext(contact, call) {
  const parts = [];
  if (contact) {
    const relation = contact.contact_type || contact.tag;
    if (relation) parts.push(relation);
    if (contact.practice_name && contact.practice_name !== `${contact.first_name} ${contact.last_name}`.trim()) {
      parts.push(contact.practice_name);
    }
  }
  if (call.company_name) parts.push(call.company_name);
  if (call.direction === "outbound") parts.push("outbound");
  return parts.join(" · ") || (call.direction === "outbound" ? "Outbound call" : "Inbound call");
}

// ═════════════ Sub-components (small, focused) ═════════════════

function ActiveCallBar() {
  const ctx = useCall() || {};
  const { activeCall, callDuration } = ctx;
  if (!activeCall) {
    // Reserve nothing — spec says: "reserve the space or animate height".
    // We animate height so sections below don't shift when a call arrives.
    return <div style={{ height: 0 }} aria-hidden />;
  }
  const isInbound = activeCall.direction === "inbound";
  const other = isInbound ? activeCall.from : activeCall.to;
  const name  = activeCall.company?.name || null;
  return (
    <div
      className="px-4 py-3 shrink-0 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-200"
      style={{ background: GOLD, color: NAVY }}
      data-testid="softphone-active-call-bar"
    >
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0 animate-pulse"
        style={{ background: RED }}
        title="Recording"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate" style={{ fontWeight: 500 }}>
          {name || formatPhoneNumber(other)}
        </p>
        <p className="text-[11px] flex items-center gap-1" style={{ fontWeight: 400 }}>
          <span>On call</span>
          <span>·</span>
          <span data-testid="softphone-recording-indicator" style={{ fontWeight: 500 }}>recording</span>
        </p>
      </div>
      <span
        className="text-sm tabular-nums shrink-0 mr-1"
        style={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
        data-testid="softphone-call-timer"
      >
        {formatDuration(callDuration || 0)}
      </span>
      <CallControls variant="compact" />
    </div>
  );
}

function Keypad({ activeCall, sendDTMF, onDigit, onBackspace }) {
  const keys = [
    { d: "1", sub: "" },        { d: "2", sub: "ABC" },  { d: "3", sub: "DEF" },
    { d: "4", sub: "GHI" },     { d: "5", sub: "JKL" },  { d: "6", sub: "MNO" },
    { d: "7", sub: "PQRS" },    { d: "8", sub: "TUV" },  { d: "9", sub: "WXYZ" },
    { d: "*", sub: "" },        { d: "0", sub: "+" },    { d: "#", sub: "" },
  ];
  // During an active call → send DTMF. Otherwise → type the digit into the
  // dial input so the pad works like a normal phone keypad.
  const inCall = !!activeCall && typeof sendDTMF === "function";
  const canPress = inCall || typeof onDigit === "function";
  const handlePress = (d) => {
    if (inCall) sendDTMF(d);
    else onDigit?.(d);
  };
  const ROYAL_BRIGHT = "#2563EB"; // brighter royal blue for digits — per Dawn's request
  return (
    // Half-width dialpad — grows/shrinks with the section. Tiles become
    // taller AND wider, digits scale up too.
    <div className="grid grid-cols-3 gap-2 flex-1 min-w-0" data-testid="softphone-keypad">
      {keys.map(k => (
        <button
          key={k.d}
          disabled={!canPress}
          onClick={() => handlePress(k.d)}
          onContextMenu={(e) => {
            // Right-click on "0" while idle → long-press equivalent to insert "+"
            if (!inCall && k.d === "0" && onDigit) { e.preventDefault(); onDigit("+"); }
          }}
          className={cn(
            "h-20 rounded-xl flex flex-col items-center justify-center leading-none",
            "transition-all shadow-sm ring-1",
            canPress
              ? "bg-gradient-to-b from-white to-slate-100 hover:from-blue-50 hover:to-white active:from-blue-100 active:to-blue-50 ring-slate-200 hover:ring-blue-300 hover:shadow-md"
              : "bg-slate-50 ring-slate-100 cursor-not-allowed"
          )}
          data-testid={`softphone-dtmf-${k.d}`}
          title={inCall ? `Send ${k.d}` : (k.d === "0" ? "Type 0 · right-click for +" : `Type ${k.d}`)}
        >
          <span
            className="text-[34px] leading-none"
            style={{
              color: canPress ? ROYAL_BRIGHT : "rgba(37,99,235,0.25)",
              fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: "-0.02em",
            }}
          >
            {k.d}
          </span>
          {k.sub && (
            <span
              className="text-[10px] mt-1 tracking-[0.18em]"
              style={{ color: canPress ? "rgba(37,99,235,0.55)" : "rgba(37,99,235,0.20)", fontWeight: 600 }}
            >
              {k.sub}
            </span>
          )}
        </button>
      ))}
      {/* Backspace tile — full-width row under the keypad; only shows when idle */}
      {!inCall && onBackspace && (
        <button
          onClick={onBackspace}
          className="col-span-3 h-9 rounded-lg text-[11px] tracking-[0.15em] uppercase text-slate-500 hover:text-slate-800 hover:bg-slate-100 ring-1 ring-slate-200 transition-colors"
          style={{ fontWeight: 600 }}
          data-testid="softphone-backspace"
          title="Delete last digit"
        >
          ← Backspace
        </button>
      )}
    </div>
  );
}

function QueueCard({ callers, contactByKey, agents, parkSlots = [], onPickup, error }) {
  return (
    <div
      className="flex-1 rounded-md bg-slate-50 overflow-hidden ring-1 ring-slate-200"
      style={{ borderLeft: `3px solid ${GOLD}`, minWidth: 0 }}
      data-testid="softphone-queue-card"
    >
      <div className="px-3 py-2">
        <p className="text-xs" style={{ fontWeight: 500 }}>
          {callers.length === 0
            ? "No callers waiting"
            : `${callers.length} caller${callers.length === 1 ? "" : "s"} waiting`}
        </p>
        {error && (
          <p className="text-[10px] text-red-600 mt-0.5">Queue endpoint unavailable</p>
        )}
        <div className="mt-1 space-y-0.5">
          {callers.slice(0, 3).map((c, i) => {
            const key = phoneKey(c.from_number);
            const ct = contactByKey.get(key);
            const nm = ct ? `${ct.first_name || ""} ${ct.last_name || ""}`.trim() : null;
            const label = nm || formatPhoneNumber(c.from_number) || "Unknown";
            const wait = c.wait_seconds || 0;
            const waitLabel = wait < 60 ? `0:${String(wait).padStart(2, "0")}` : formatDuration(wait);
            return (
              <p key={c.id || i} className="text-[11px] text-slate-600 truncate" style={{ fontWeight: 400 }} data-testid={`softphone-queue-caller-${i}`}>
                {label} · {nm ? (ct?.contact_type || "") : "unknown"} · {waitLabel}
              </p>
            );
          })}
        </div>
      </div>

      <div className="px-3 py-1.5 border-t" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
        {agents === "forbidden" ? (
          <p className="text-[11px] text-slate-400" style={{ fontWeight: 400 }}>Available agents · admin only</p>
        ) : agents === null ? (
          <p className="text-[11px] text-slate-400" style={{ fontWeight: 400 }}>Agents · loading…</p>
        ) : (
          <p className="text-[11px] text-slate-600" style={{ fontWeight: 400 }}>
            {(agents.length || 0)} agents available{(agents || []).length > 0 && ` · ${agents.slice(0, 3).map(a => a.first_name).filter(Boolean).join(", ")}`}
          </p>
        )}
      </div>

      {parkSlots && parkSlots.length > 0 && (
        <div className="px-3 py-1.5 border-t space-y-1" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          {parkSlots.filter(s => s.occupied).map(s => {
            const key = phoneKey(s.caller_number);
            const ct = contactByKey.get(key);
            const nm = ct ? `${ct.first_name || ""} ${ct.last_name || ""}`.trim() : null;
            return (
              <button
                key={s.slot}
                onClick={() => onPickup && onPickup(s.slot)}
                className="w-full text-left rounded-sm px-1 py-1 hover:bg-slate-50 transition-colors"
                data-testid={`softphone-park-${s.slot}`}
              >
                <p className="text-[11px] truncate" style={{ fontWeight: 500 }}>
                  Park {s.slot} · {nm || formatPhoneNumber(s.caller_number) || "Unknown"}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SpeedDials({ items, onCall, onAdd }) {
  const filled = items.slice(0, 7);
  const empties = Math.max(0, 7 - filled.length);
  return (
    <div className="px-4 py-2 shrink-0" data-testid="softphone-speed-dials">
      <div className="grid grid-cols-4 gap-1.5">
        {filled.map(sd => (
          <button
            key={sd.id}
            onClick={() => onCall(sd.phone_number)}
            className="h-10 rounded-md bg-slate-100 text-sm truncate px-2 hover:bg-slate-200 transition-colors shadow-sm"
            style={{ color: NAVY, fontWeight: 500 }}
            title={`${sd.name} · ${formatPhoneNumber(sd.phone_number)}`}
            data-testid={`softphone-speed-dial-${sd.id}`}
          >
            {sd.name}
          </button>
        ))}
        {Array.from({ length: empties }).map((_, i) => (
          <div key={`empty-${i}`} className="h-10 rounded-md bg-transparent" />
        ))}
        <button
          onClick={onAdd}
          className="h-10 rounded-md flex items-center justify-center gap-1 text-xs border-dashed border-2 hover:border-slate-400 transition-colors"
          style={{ borderColor: "rgba(15,23,42,0.18)", color: "rgba(15,23,42,0.55)", fontWeight: 500 }}
          data-testid="softphone-speed-dial-add"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      className={cn("h-8 px-3 rounded-full text-xs flex items-center gap-1.5 transition-colors")}
      style={
        active
          ? { background: NAVY, color: "white", fontWeight: 500 }
          : { background: "transparent", color: "rgba(15,23,42,0.72)", fontWeight: 400 }
      }
      data-testid={testid}
      data-active={active}
    >
      {label}
      {count > 0 && (
        <span
          className="rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center text-[10px]"
          style={{ background: GOLD, color: NAVY, fontWeight: 500 }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function FeedRow({ item, zebra, onOpen, onCall, onText, onMarkHandled, unknownNumber }) {
  // Accent bar carries urgency; zebra is a separate signal.
  const accent = (() => {
    if (!item.unattended && !unknownNumber) return null;
    if (item.type === "missed_call" && item.unattended) return RED;
    if ((item.type === "voicemail" || item.type === "sms") && item.unattended) return ROYAL;
    if (unknownNumber && item.unattended) return "#F59E0B";
    return null;
  })();

  const channelIconColor = (() => {
    if (item.type === "missed_call") return RED;
    if (item.type === "voicemail" && item.unattended) return ROYAL;
    if (item.type === "sms" && item.unattended) return ROYAL;
    return "rgba(15,23,42,0.42)";
  })();

  const Ch = item.type === "missed_call" ? PhoneMissed
    : item.type === "voicemail" ? Voicemail
    : item.type === "sms" ? MessageSquare
    : Phone;

  return (
    <div
      className="relative px-4 py-2.5 flex items-center gap-2.5 cursor-pointer transition-colors group border-b border-slate-100 last:border-b-0"
      style={{ background: zebra ? "#F5F8FC" : "transparent" }}
      onClick={onOpen}
      data-testid={`softphone-feed-row-${item.id}`}
      data-type={item.type}
      data-unattended={item.unattended || false}
    >
      {/* Left accent bar — thicker (3px) for stronger urgency signal */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ background: accent || "transparent" }}
        data-testid={`softphone-feed-accent-${item.id}`}
      />
      {/* Channel icon */}
      <Ch className="w-4 h-4 shrink-0" style={{ color: channelIconColor }} />
      {/* Name + context — darker text for real contrast */}
      <div className="flex-1 min-w-0">
        <p
          className="text-sm truncate"
          style={{
            fontWeight: item.unattended ? 600 : 500,
            color: item.unattended ? "#0F172A" : "#1E293B",
          }}
        >
          {item.name || formatPhoneNumber(item.phone) || "Unknown"}
        </p>
        <p className="text-[11px] truncate" style={{ fontWeight: 500, color: "#475569" }}>
          {item.contextLine}
        </p>
      </div>
      {/* Timestamp */}
      <span
        className="text-[11px] shrink-0 text-right tabular-nums"
        style={{ width: 44, fontWeight: 500, fontVariantNumeric: "tabular-nums", color: "#64748B" }}
      >
        {shortTime(item.timestamp)}
      </span>
      {/* Inline actions */}
      <div className="flex items-center gap-1 shrink-0 opacity-100">
        {item.type === "voicemail" && (
          <ActionBtn label="Play" onClick={(e) => { e.stopPropagation(); onOpen(); }} color={GREEN} testid={`softphone-vm-play-${item.rawId}`}>
            <Play className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        {(item.type === "call" || item.type === "missed_call" || item.type === "voicemail" || item.type === "sms") && (
          <ActionBtn label="Call back" onClick={(e) => { e.stopPropagation(); onCall(); }} color={GREEN} testid={`softphone-callback-${item.rawId || item.id}`}>
            <PhoneCall className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        <ActionBtn label="Text" onClick={(e) => { e.stopPropagation(); onText(); }} color={ROYAL} testid={`softphone-text-${item.id}`}>
          <MessageSquare className="w-3.5 h-3.5" />
        </ActionBtn>
        {unknownNumber && (
          <ActionBtn
            label="Name this number"
            onClick={(e) => {
              e.stopPropagation();
              // Surface the existing needs-rename flow inline.
              window.dispatchEvent(new CustomEvent("softphone:name-unknown", { detail: { phone: item.phone } }));
              toast.info(`Add “${formatPhoneNumber(item.phone)}” to contacts`);
            }}
            color={ROYAL}
            testid={`softphone-name-${item.id}`}
          >
            <UserPlus className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        {item.unattended && (
          <ActionBtn label="Mark handled" onClick={(e) => { e.stopPropagation(); onMarkHandled(); }} color="rgba(15,23,42,0.55)" testid={`softphone-handled-${item.id}`}>
            <Check className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, color, label, testid }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-black/5 transition-colors"
      style={{ color }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
